import React, { useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import Carousel from "react-material-ui-carousel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CarouselLeftButton from "../icons/CarouselLeftButton";
import CarouselRightButton from "../icons/CarouselRightButton";
import { convertDateTimeToString } from "common/format";
// yet-another-react-lightbox uses package `exports` map for subpaths, which Webpack 4 does not
// support. We import from dist/ directly as a workaround. The cast below is required because
// TypeScript 4 (moduleResolution: node) cannot resolve .js extension imports inside the zoom
// plugin's .d.ts, so the zoom module-augmentation that adds the `zoom` prop to LightboxProps is
// never applied.
import _Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/dist/plugins/zoom";
import "yet-another-react-lightbox/dist/styles.css";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Lightbox = _Lightbox as React.ComponentType<any>;
import { SlideItem } from "./SlideItem";
import type { CarouselSlide } from "./SlideItem";

// Re-export so consumers can import CarouselSlide from this barrel entry point
export type { CarouselSlide };

// useZoom is not exported from the zoom plugin's dist bundle — the only public API for accessing
// ZoomRef is via render.buttonZoom, which receives it as props. We use position:fixed to escape
// the toolbar flow and render the buttons at the bottom-right of the viewport.
interface FloatingZoomControlsProps {
  zoom: number;
  maxZoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  disabled: boolean;
}

function FloatingZoomControls({ zoom, maxZoom, zoomIn, zoomOut, disabled }: FloatingZoomControlsProps) {
  return (
    <Box sx={{ position: "fixed", bottom: 80, right: 16, display: "flex", flexDirection: "column", gap: 1, zIndex: 1 }}>
      <IconButton
        onClick={zoomIn}
        disabled={disabled || zoom >= maxZoom}
        size="large"
        aria-label="Zoom in"
        sx={{
          backgroundColor: "rgba(0,0,0,0.45)",
          color: "white",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" },
          "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
        }}
      >
        <AddCircleOutlineIcon />
      </IconButton>
      <IconButton
        onClick={zoomOut}
        disabled={disabled || zoom <= 1}
        size="large"
        aria-label="Zoom out"
        sx={{
          backgroundColor: "rgba(0,0,0,0.45)",
          color: "white",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" },
          "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
        }}
      >
        <RemoveCircleOutlineIcon />
      </IconButton>
    </Box>
  );
}

export interface ImageCarouselProps {
  slides: CarouselSlide[];
  /** Carousel height in px. Default: 404 */
  height?: number;
  /** Show loading skeleton. Default: false */
  loading?: boolean;
  /** Enable full-screen lightbox on slide click. Default: true */
  lightbox?: boolean;
  /** Enable magnifying-glass on desktop hover. Default: true */
  magnifyingGlass?: boolean;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  slides,
  height,
  loading = false,
  lightbox = true,
  magnifyingGlass = true,
}) => {
  const isXsScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const effectiveHeight = height ?? (isXsScreen ? 200 : isSmScreen ? 300 : 404);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (loading) {
    return (
      <Skeleton variant="rectangular" width="100%">
        <Box width="100%" height={effectiveHeight} />
      </Skeleton>
    );
  }

  if (slides.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: effectiveHeight,
          background: "linear-gradient(180deg, #e0e0e0 0%, #bdbdbd 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary" variant="body2">
          No Images
        </Typography>
      </Box>
    );
  }

  // Clamp to valid range in case slides prop shrinks while carousel is open
  const safeIndex = Math.min(currentIndex, Math.max(0, slides.length - 1));

  return (
    <>
      <Carousel
        autoPlay={false}
        swipe={true}
        navButtonsAlwaysVisible={true}
        indicators={false}
        PrevIcon={<CarouselLeftButton />}
        NextIcon={<CarouselRightButton />}
        navButtonsProps={{ style: { backgroundColor: "transparent", padding: 0 } }}
        height={effectiveHeight}
        index={safeIndex}
        onChange={(now) => setCurrentIndex(now ?? 0)}
      >
        {slides.map((slide, index) => (
          <SlideItem
            key={`${index}:${slide.imageSrc}`}
            slide={slide}
            magnifyingGlass={magnifyingGlass}
            onClick={lightbox ? () => setLightboxOpen(true) : undefined}
          />
        ))}
      </Carousel>
      {lightbox && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={safeIndex}
          slides={slides.map((s) => ({ src: s.imageSrc }))}
          plugins={[Zoom]}
          zoom={{ maxZoomPixelRatio: 4, zoomInMultiplier: 2, doubleClickMaxStops: 2, scrollToZoom: false }}
          toolbar={{ buttons: ["close"] }}
          render={{
            buttonZoom: (props: FloatingZoomControlsProps) => <FloatingZoomControls {...props} />,
            controls: () => {
              const slide = slides[safeIndex];
              return (
                <Box sx={{ position: "absolute", top: 20, left: 20, p: 0, pointerEvents: "none" }}>
                  <Typography color="white" fontSize="1rem" fontWeight={600}>
                    {slide?.title}
                    {slide?.date ? `: ${convertDateTimeToString(slide.date)}` : ""}
                  </Typography>
                </Box>
              );
            },
          }}
          carousel={{ finite: true }}
          controller={{ closeOnBackdropClick: true }}
          on={{ view: ({ index }: { index: number }) => setCurrentIndex(Math.min(index, slides.length - 1)) }}
        />
      )}
    </>
  );
};

export default ImageCarousel;
